import React, { useState, useEffect, useRef } from 'react';
import { Paginator } from 'primereact/paginator';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import ComplaintService, {
    CustomerComplaintFilter,
    CustomerComplaintResponse,
    CustomerProductComplaintItem,
    CustomerServiceComplaintItem
} from '../../services/complaint/ComplaintService';
import {
    getComplaintStatus,
    getCustomerComplaintById,
    updateCustomerComplaint
} from '../../services/complaint/ComplaintService';
import { ComplaintQuickViewResponse, UpdateComplaintRequest } from '../../services/order/order.service';
import ComplaintQuickViewDialog from './ComplaintQuickViewDialog';
import ComplaintEditDialog from './ComplaintEditDialog';
import CustomerserviceComplaintregister from './CustomerserviceComplaintregister';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import AppLoading from '../app-loading/app-loading';
import './MyComplaints.css';

const MyComplaints: React.FC = () => {
    // Refs
    const menuRefMap = useRef<{ [key: number]: Menu | null }>({});

    // State
    const [quickViewVisible, setQuickViewVisible] = useState(false);
    const [editVisible, setEditVisible] = useState(false);
    const [addComplaintVisible, setAddComplaintVisible] = useState(false);
    const [selectedComplaintId, setSelectedComplaintId] = useState<number | null>(null);
    const [selectedComplaint, setSelectedComplaint] = useState<ComplaintQuickViewResponse | null>(null);

    const [loading, setLoading] = useState(false);
    const [complaints, setComplaints] = useState<CustomerComplaintResponse | null>(null);
    const [statuses, setStatuses] = useState<any[]>([]);

    const [filters, setFilters] = useState<CustomerComplaintFilter>({
        pageNumber: 1,
        pageSize: 10,
        complaintTypeId: null,
        statusId: null,
        startDate: null,
        endDate: null,
        sortBy: '',
        sortOrder: null,
    });

    const complaintTypes = [
        { label: 'Product', value: 2 },
        { label: 'Service', value: 3 }
    ];

    useEffect(() => {
        loadStatuses();
    }, []);

    useEffect(() => {
        loadComplaints();
    }, [filters]);

    // Helpers
    const mapMenuRef = (el: Menu | null, id: number) => {
        menuRefMap.current[id] = el;
    };

    const toggleMenu = (event: any, id: number) => {
        menuRefMap.current[id]?.toggle(event);
    };

    const loadStatuses = async () => {
        try {
            const statusList = await getComplaintStatus();
            const statusArray = Array.isArray(statusList) ? statusList : [];
            const mappedStatuses = statusArray.map((status: any) => ({
                label: String(status.status || status.name || 'Unknown'),
                value: Number(status.complaintStatusId || status.id || status.statusId)
            }));
            setStatuses(mappedStatuses);
        } catch (error) {
            console.error('Error loading statuses:', error);
            setStatuses([]);
        }
    };

    const loadComplaints = async () => {
        setLoading(true);
        try {
            const response = await ComplaintService.getCustomerComplaints(filters);
            setComplaints(response);
        } catch (error) {
            console.error('Error loading complaints:', error);
            setComplaints(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setFilters({ ...filters, pageNumber: 1 });
    };

    const handleReset = () => {
        setFilters({
            pageNumber: 1,
            pageSize: 10,
            complaintTypeId: null,
            statusId: null,
            startDate: null,
            endDate: null,
            sortBy: '',
            sortOrder: null,
        });
    };

    const onPageChange = (event: any) => {
        setFilters({
            ...filters,
            pageNumber: Math.floor(event.first / event.rows) + 1,
            pageSize: event.rows,
        });
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';

        // Treat as UTC if no timezone specified
        const dateStr = (dateString.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateString))
            ? dateString
            : `${dateString}Z`;

        return new Date(dateStr).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata'
        });
    };
    const handleQuickView = (rowData: any) => {
        setSelectedComplaintId(rowData.complaintId);
        setQuickViewVisible(true);
    };

    const handleEdit = async (rowData: any) => {
        try {
            setLoading(true);
            const fullComplaint = await getCustomerComplaintById(rowData.complaintId);
            const mappedComplaint = {
                ...fullComplaint,
                createdDate: fullComplaint.createdDate || fullComplaint.createdOn,
                modifiedDate: fullComplaint.modifiedDate || fullComplaint.lastModifiedOn,
                attachments: fullComplaint.attachments || fullComplaint.Attachments || [],
                priority: fullComplaint.priority || fullComplaint.Priority,
                statusId: fullComplaint.statusId || fullComplaint.StatusId,
                complaintTypeId: fullComplaint.complaintTypeId || fullComplaint.ComplaintTypeId,
            };

            setSelectedComplaint(mappedComplaint);
            setEditVisible(true);
        } catch (error) {
            console.error("Error fetching complaint details for edit", error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditSave = async (data: UpdateComplaintRequest) => {
        // Map UpdateComplaintRequest (camelCase) to UpdateCustomerComplaintRequestDTO (PascalCase)
        const requestDTO: any = {
            Id: data.id,
            ComplaintTypeId: data.complaintTypeId,
            Description: data.description,
            Priority: data.priority,
            StatusId: data.statusId,
            Remarks: (data as any).remarks || '',

            // Internal/Action fields
            ActionDescription: data.actionDescription,
            CorrectiveAction: data.correctiveAction,
            PreventiveAction: data.preventiveAction,
            ActionDate: data.actionDate,
            ActionBy: data.actionBy,
            CostPerQtyInRs: data.costPerQtyInRs,

            // Type specific
            CustomerId: data.customerId,
            InvoiceId: data.invoiceId,
            OrderDetailId: data.orderDetailId,

            Attachments: data.attachments
        };

        return await updateCustomerComplaint(requestDTO);
    };

    const getDisplayData = () => {
        if (!complaints) return [];

        // If no type selected, show ALL (merge product and service)
        if (!filters.complaintTypeId) {
            const prod = complaints.productComplaints || (complaints as any).ProductComplaints || (complaints as any).data || [];
            const serv = complaints.serviceComplaints || (complaints as any).ServiceComplaints || [];
            return [...prod, ...serv];
        }

        // Handle string/number mismatch with loose equality
        if (filters.complaintTypeId == 2) {
            const products = complaints.productComplaints || (complaints as any).ProductComplaints || (complaints as any).data || [];
            return products;
        } else {
            const services = complaints.serviceComplaints || (complaints as any).ServiceComplaints || [];
            return services;
        }
    };

    // Helper for Time Ago
    const timeAgo = (dateStr: string) => {
        if (!dateStr) return '';

        // Parse as local time (IST)
        const date = new Date(dateStr.replace(' ', 'T'));
        const now = new Date();

        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays <= 0) return 'Today';
        return `${diffDays}d old`;
    };

    // Component: Complaint Card
    const ComplaintCard = ({ item }: { item: any }) => {
        const isProduct = item.productName || item.invoiceNo || (filters.complaintTypeId === 2);
        // Use generic icons but keep the distinct visual logic if needed, or unify.
        // Green Theme -> Uniform Clean Look
        const typeIcon = isProduct ? 'pi-box' : 'pi-wrench';

        let title = isProduct ? (item.productName || 'Product Issue') : 'Service Request';
        if (!isProduct && item.description && item.description.length < 50) {
            title = item.description;
        }

        const rawStatus = item.status?.toLowerCase().replace(/\s+/g, '') || 'new';
        const statusClass = `status-${rawStatus}`;
        const dateVal = item.complaintDate || item.createdDate || item.createdOn;
        const createdBy = item.createdBy || item.customerName || 'Unknown';
        const createdByInitial = createdBy.charAt(0).toUpperCase();

        return (
            <div className={`complaint-card ${statusClass}`}>
                <div className="card-icon-box">
                    <i className={`pi ${typeIcon}`}></i>
                </div>

                <div className="card-content">
                    <div className="flex align-items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-2 py-1 border-round">CMP-{item.complaintId}</span>
                        {item.invoiceNo && (
                            <span className="text-sm text-gray-500">
                                Inv: <span className="font-medium text-gray-700">{item.invoiceNo}</span>
                            </span>
                        )}
                    </div>

                    <h3 className="card-title">{title}</h3>

                    <div className="card-meta">
                        <div className="meta-item bg-gray-50 px-2 py-1 border-round border-1 border-gray-200">
                            <span className="w-1rem h-1rem bg-blue-100 text-blue-600 border-circle flex align-items-center justify-content-center text-xs font-bold mr-1">
                                {createdByInitial}
                            </span>
                            <span className="text-gray-700 font-medium text-xs">{createdBy}</span>
                        </div>

                        <div className="meta-item">
                            <i className="pi pi-clock text-green-500"></i>
                            <span>{timeAgo(dateVal)}</span>
                        </div>

                        {item.invoiceValue && (
                            <div className="meta-item">
                                <i className="pi pi-dollar text-green-500"></i>
                                <span className="font-bold text-gray-700">₹{item.invoiceValue?.toLocaleString('en-IN')}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="card-actions flex flex-column align-items-end gap-2">
                    <span className={`status-badge-modern ${rawStatus}`}>
                        {item.status}
                    </span>
                    <div className="flex gap-1">
                        <Button
                            icon="pi pi-eye"
                            className="p-button-rounded p-button-text action-btn-modern"
                            aria-label="View"
                            onClick={() => handleQuickView(item)}
                        />
                        {rawStatus === 'new' && (
                            <Button
                                icon="pi pi-pencil"
                                className="p-button-rounded p-button-text action-btn-modern"
                                aria-label="Edit"
                                onClick={() => handleEdit(item)}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="my-complaints-page">
            <style>{`
                /* Custom Dropdown Panel Style for MyComplaints */
                .my-complaints-dropdown-panel.p-dropdown-panel {
                    border-radius: 12px !important;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
                    border: 1px solid #f0f0f0 !important;
                    padding: 0.5rem !important;
                }
                .my-complaints-dropdown-panel.p-dropdown-panel .p-dropdown-items {
                    padding: 0 !important;
                }
                .my-complaints-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item {
                    border-radius: 8px !important;
                    margin: 2px 0 !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.875rem !important;
                    transition: all 0.2s ease !important;
                }
                .my-complaints-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
                    background: #4CAF50 !important;
                    color: #ffffff !important;
                }
                .my-complaints-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
                    background: #4CAF50 !important;
                    color: #ffffff !important;
                    font-weight: 600 !important;
                }
                .my-complaints-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
                    background: #4CAF50 !important;
                    color: #ffffff !important;
                    font-weight: 600 !important;
                }

                /* Responsive Design */
                @media screen and (max-width: 960px) {
                    .filter-controls {
                        flex-wrap: wrap;
                    }
                    .filter-dropdown,
                    .filter-calendar {
                        flex: 1 1 calc(50% - 0.5rem) !important;
                        width: auto !important;
                    }
                    .reset-button {
                        margin-left: auto;
                    }
                }

                @media screen and (max-width: 576px) {
                    .complaint-header-section {
                        flex-direction: column;
                        align-items: flex-start !important; /* Override default center */
                    }
                    
                    .header-left {
                        flex-direction: column;
                        align-items: flex-start !important;
                        gap: 1rem;
                    }

                    .filter-controls {
                        flex-direction: column;
                        gap: 0.75rem;
                    }

                    .filter-dropdown,
                    .filter-calendar {
                        width: 100% !important;
                        flex: 1 1 100% !important;
                    }

                    .reset-button {
                        width: 100%;
                        margin-top: 0.5rem;
                    }
                    
                    .complaint-card {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    
                    .card-icon-box {
                        margin-bottom: 1rem;
                    }
                    
                    .card-content {
                        width: 100%;
                        margin-bottom: 1rem;
                        padding-right: 0;
                        border-right: none;
                    }
                    
                    .card-actions {
                        width: 100%;
                        flex-direction: row;
                        justify-content: space-between;
                        align-items: center;
                        border-top: 1px solid #f0f0f0;
                        padding-top: 1rem;
                        margin-top: 0.5rem;
                    }
                }
            `}</style>
            {/* Header Section */}
            <div className="complaint-header-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className="header-left">
                    <div className="header-icon">
                        <i className="pi pi-megaphone"></i>
                    </div>
                    <div className="header-text">
                        <h1> My Complaints</h1>
                        <p>Track, manage, and resolve customer complaints efficiently</p>
                        {/* <div className="total-count">
                            <i className="pi pi-chart-bar"></i>
                            <span>{complaints?.totalRecords || 0} Total Complaints</span>
                        </div> */}
                    </div>
                </div>
                <Button
                    label="Raise Service Complaint"
                    icon="pi pi-plus"
                    onClick={() => setAddComplaintVisible(true)}
                    style={{
                        backgroundColor: 'rgb(76, 175, 80)',
                        border: 'none',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '0.6rem 1.2rem',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        whiteSpace: 'nowrap'
                    }}
                />
            </div>

            {/* Filter Section */}
            <div className="filter-section">
                <div className="filter-header">
                    <i className="pi pi-filter"></i>
                    <span>Filter Complaints</span>
                </div>
                <div className="filter-controls">
                    <Dropdown
                        value={filters.complaintTypeId}
                        options={complaintTypes}
                        onChange={(e) => setFilters({ ...filters, complaintTypeId: e.value, pageNumber: 1 })}
                        placeholder="Type"
                        className="filter-dropdown"
                        showClear
                        panelClassName="my-complaints-dropdown-panel"
                    />
                    <Dropdown
                        value={filters.statusId}
                        options={statuses}
                        onChange={(e) => setFilters({ ...filters, statusId: e.value })}
                        placeholder="Status"
                        className="filter-dropdown"
                        showClear
                        panelClassName="my-complaints-dropdown-panel"
                    />
                    <Calendar
                        value={filters.startDate ? new Date(filters.startDate) : null}
                        onChange={(e) => {
                            const dateValue = Array.isArray(e.value) ? e.value[0] : e.value;
                            setFilters({
                                ...filters,
                                startDate: dateValue instanceof Date ?
                                    `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}-${String(dateValue.getDate()).padStart(2, '0')}`
                                    : null
                            });
                        }}
                        dateFormat="dd/mm/yy"
                        placeholder="Start Date"
                        className="filter-calendar compact-calendar"
                        panelClassName="my-complaints-calendar-panel"
                        showIcon
                        icon="pi pi-calendar"
                        readOnlyInput
                        maxDate={filters.endDate ? new Date(filters.endDate) : undefined}
                    />
                    <Calendar
                        value={filters.endDate ? new Date(filters.endDate) : null}
                        onChange={(e) => {
                            const dateValue = Array.isArray(e.value) ? e.value[0] : e.value;
                            setFilters({
                                ...filters,
                                endDate: dateValue instanceof Date ?
                                    `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}-${String(dateValue.getDate()).padStart(2, '0')}`
                                    : null
                            });
                        }}
                        dateFormat="dd/mm/yy"
                        placeholder="End Date"
                        className="filter-calendar compact-calendar"
                        panelClassName="my-complaints-calendar-panel"
                        showIcon
                        icon="pi pi-calendar"
                        readOnlyInput
                        minDate={filters.startDate ? new Date(filters.startDate) : undefined}
                    />
                    <Button
                        icon="pi pi-refresh"
                        className="mc-reset-button"
                        onClick={handleReset}
                        tooltip="Reset"
                    />
                </div>
            </div>

            {/* Results Section: Card List + Paginator */}
            <div className="results-section">
                {complaints?.totalRecords === 0 && !loading ? (
                    <div className="flex flex-column align-items-center justify-content-center p-6 bg-white border-round-xl border-1 border-gray-200">
                        <div className="bg-green-50 p-4 border-circle mb-3">
                            <i className="pi pi-inbox text-4xl text-green-500"></i>
                        </div>
                        <span className="text-xl font-semibold text-gray-700">No complaints found</span>
                        <p className="text-gray-500 mt-2">Try adjusting your filters or search criteria.</p>
                    </div>
                ) : (
                    <div className="complaint-card-list">
                        {getDisplayData().map((item: any) => (
                            <ComplaintCard key={item.complaintId} item={item} />
                        ))}
                    </div>
                )}

                <Paginator
                    first={(filters.pageNumber - 1) * filters.pageSize}
                    rows={filters.pageSize}
                    totalRecords={complaints?.totalRecords || 0}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={onPageChange}
                    className="custom-paginator mt-4"
                />
            </div>

            {loading && <AppLoading />}

            {/* Dialogs */}
            <ComplaintQuickViewDialog
                visible={quickViewVisible}
                complaintId={selectedComplaintId}
                onHide={() => setQuickViewVisible(false)}
                fetchDetails={getCustomerComplaintById}
            />

            <ComplaintEditDialog
                visible={editVisible}
                complaint={selectedComplaint}
                onHide={() => setEditVisible(false)}
                onSuccess={() => {
                    loadComplaints(); // Refresh list
                }}
                onSave={handleEditSave}
            />
            {/* Add Service Complaint Modal */}
            <Dialog
                header="Raise Service Complaint"
                visible={addComplaintVisible}
                onHide={() => setAddComplaintVisible(false)}
                style={{ width: '480px' }}
                breakpoints={{ '640px': '95vw' }}
                modal
                draggable={false}
                resizable={false}
                pt={{
                    header: {
                        style: {
                            background: 'linear-gradient(135deg, #4CAF50, #388e3c)',
                            color: '#ffffff',
                            padding: '1.25rem 1.75rem',
                            borderRadius: '12px 12px 0 0',
                            borderBottom: 'none'
                        }
                    },
                    content: { style: { padding: '0', background: '#ffffff', borderRadius: '0 0 12px 12px' } },
                    closeButton: { style: { color: '#ffffff' } }
                }}
            >
                <CustomerserviceComplaintregister
                    onSuccess={() => {
                        setAddComplaintVisible(false);
                        loadComplaints();
                    }}
                />
            </Dialog>
        </div>
    );
};

export default MyComplaints;
